import { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Flex, Button, Alert, Text } from '@nula/core-ui';
import styles from './subscribe-panel.module.css';

export function SubscribePanel({
  clientSecret,
  onSuccess,
}: {
  clientSecret: string;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    // Nothing due today — a trial, or a 100%-off code — means the server hands
    // back a SetupIntent (card saved for later) rather than a PaymentIntent
    // (card charged now). confirmPayment cannot confirm a SetupIntent: it
    // neither succeeds nor errors, so the user sits on a spinner forever.
    const result = clientSecret.startsWith('seti_')
      ? await stripe.confirmSetup({ elements, redirect: 'if_required' })
      : await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (result.error) {
      setError(result.error.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onSuccess();
  };

  return (
    <Flex vertical gap="md" className={styles.panel}>
      <Text size="sm" type="secondary">
        Enter your card to start your Pro subscription.
      </Text>
      <PaymentElement />
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        variant="primary"
        pressEffect="glow"
        loading={submitting}
        disabled={!stripe || !elements || submitting}
        onClick={handleSubmit}
      >
        Subscribe
      </Button>
    </Flex>
  );
}
